import { useI18N, useKeyPress } from "@sundae/react-hooks";
import { Button, CheckCircledIcon, Confetti, Text } from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, PropsWithChildren, ReactNode, useEffect, useState } from "react";

import { useProfileAppContext } from "../../../stores";

export interface IOrderSuccessProps {
  actionButtonText?: string;
  className?: string;
  message?: ReactNode;
  onActionButtonClick?(): void;
  onCancel(): void;
  onSuccess?(): void;
  onSuccessButtonText?: string;
  withConfetti?: boolean;
}

const OrderSuccess: FC<PropsWithChildren<IOrderSuccessProps>> = ({
  actionButtonText,
  className,
  message,
  onActionButtonClick,
  onCancel,
  onSuccess,
  onSuccessButtonText,
  withConfetti = true,
}) => {
  const { t } = useI18N("transactions");
  const [fireConfetti, setFireConfetti] = useState(false);
  const {
    state: { prefersReducedMotion },
  } = useProfileAppContext();

  useEffect(() => {
    setTimeout(() => setFireConfetti(true), 500);
  }, []);

  // reset widget by hitting ESC
  useKeyPress("esc", onCancel);

  return (
    <>
      <div className="relative z-10 h-full">
        <div className="flex h-full flex-col items-center justify-center gap-4 text-center xl:gap-5">
          <div>
            <CheckCircledIcon
              className={cx(
                "h-12 w-12 fill-current xl:h-20 xl:w-20",
                "text-success dark:text-success"
              )}
            />
          </div>

          <div className={cx("grid gap-4 xl:gap-5", className)}>
            <Text tag="p" size="sm">
              {message ?? t(`transaction.success`)}
            </Text>
            <div className="flex justify-center gap-4">
              <Button onClick={onSuccess} size="sm" variant="neutral">
                {onSuccessButtonText ?? t(`transaction.success_view`, { context: "view" })}
              </Button>

              {actionButtonText && onActionButtonClick && (
                <Button onClick={onActionButtonClick} size="sm" variant="primary">
                  {actionButtonText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {!prefersReducedMotion && withConfetti && (
        <Confetti
          particleCount={150}
          className="!absolute bottom-0 left-0"
          fire={fireConfetti}
          spread={55}
          origin={{ y: 1.05 }}
        />
      )}
    </>
  );
};

export default OrderSuccess;
